/**
 * ContractorPrimaryAddressViewElement — „Adres macierzysty" jako JEDNA formatka
 * (Model B v1) w kreatorze layoutu kartoteki Kontrahenci.
 *
 * W builderze (mode='builder') renderuje się jako PEŁNOWYMIAROWA formatka: nagłówek
 * + siatka 2-kol z polami adresu (Kraj/Ulica/Nr budynku/…), każde pole tak szerokie
 * jak zwykłe pole (Nazwa/KRS). Każde pole ma inline ikonkę oka — kliknięcie chowa/
 * pokazuje je, zapisując `settings.hiddenFields` przez `onSettingsChange` (rozszerzenie
 * kontraktu renderera, tylko mode='builder'). Bez developera i bez wchodzenia w panel
 * właściwości. `fullWidth: true` → col-span-full w gridzie sekcji.
 *
 * Na REALNEJ karcie kontrahenta blok dispatchowany przez
 * `contractorContextRenderers['contractor_primary_address_view']`
 * (→ `ContractorPrimaryAddressSection`), który respektuje te same `hiddenFields`.
 * `allowedModules: ['contractors']` — tylko dla Kontrahentów.
 */

import { MapPin } from 'lucide-react';

import {
  ADDRESS_CORE_FIELD_I18N_SUFFIX,
  ADDRESS_CORE_FIELD_ORDER,
  type AddressCoreField,
  parseHiddenAddressCoreFields,
} from '@/components/addresses/AddressCoreFields';
import { Checkbox } from '@/components/Checkbox';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';
import { applyOrder, parseSlugList, reorderSlugs } from '@/lib/kartoteki/formatka-order';

import { FormatkaReorderList } from './FormatkaReorderList';

type AddressFieldKey = AddressCoreField;

function fieldLabel(field: AddressFieldKey): string {
  return i18n.t(`tenant.addresses.coreField.${ADDRESS_CORE_FIELD_I18N_SUFFIX[field]}`);
}

/** Podpowiedź typu pola (mirror stylu „name · varchar" na kartach pól). */
const FIELD_KIND_HINT: Record<AddressFieldKey, string> = {
  countryCode: 'słownik',
  region: 'słownik',
  street: 'tekst',
  buildingNo: 'tekst',
  apartmentNo: 'tekst',
  postalCode: 'tekst',
  city: 'tekst',
};

export const ContractorPrimaryAddressViewElement: LayoutElementDefinition = {
  type: 'contractor_primary_address_view',
  category: 'tresc',
  isContainer: false,
  fullWidth: true,
  label: 'Adres macierzysty',
  icon: 'map-pin',
  allowedModules: ['contractors'],
  defaultSettings: () => ({ hiddenFields: [] }),
  propertiesEditor: ({ settings, onChange }) => (
    <ContractorPrimaryAddressConfigEditor settings={settings} onChange={onChange} />
  ),
  render: ({ element, mode, onSettingsChange, systemHideOnly }) => {
    const hidden = new Set<string>(parseHiddenAddressCoreFields(element.settings));
    // Kolejność pól z `settings.fieldOrder` (drag w formatce); nowe pola widoczne z automatu.
    const orderedSlugs = applyOrder(
      ADDRESS_CORE_FIELD_ORDER,
      parseSlugList(element.settings, 'fieldOrder'),
    );
    const canHide = mode === 'builder' && !!onSettingsChange;
    // Systemowy layout: tylko ukrywanie (bez reorderu), oko przebija inertny canvas.
    const canReorder = canHide && !systemHideOnly;

    const items = orderedSlugs.map((slug) => {
      const field = slug as AddressFieldKey;
      return { slug, label: fieldLabel(field), hint: FIELD_KIND_HINT[field] };
    });

    return (
      <FormatkaReorderList
        elementId={element.id}
        items={items}
        hiddenSlugs={hidden}
        canHide={canHide}
        canReorder={canReorder}
        pierceLock={!!systemHideOnly}
        onReorder={(active, over) =>
          onSettingsChange?.({ fieldOrder: reorderSlugs(orderedSlugs, active, over) })
        }
        onToggleHidden={(slug) => {
          const next = new Set(hidden);
          if (next.has(slug)) next.delete(slug);
          else next.add(slug);
          onSettingsChange?.({ hiddenFields: Array.from(next) });
        }}
        header={
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <MapPin className="h-4 w-4 text-text-muted" aria-hidden={true} />
            {i18n.t('tenant.contractors.primaryAddressBlock.paletteLabel', {
              defaultValue: 'Adres macierzysty',
            })}
          </p>
        }
        hiddenLabel={i18n.t('tenant.contractors.primaryAddressBlock.fieldHidden', {
          defaultValue: 'ukryte',
        })}
        labels={{
          hide: i18n.t('tenant.contractors.primaryAddressBlock.hideField', {
            defaultValue: 'Ukryj pole',
          }),
          show: i18n.t('tenant.contractors.primaryAddressBlock.showField', {
            defaultValue: 'Pokaż pole',
          }),
          drag: i18n.t('tenant.formatkaReorder.dragHint', {
            defaultValue: 'Przeciągnij, aby zmienić kolejność',
          }),
        }}
        testId={`contractor-primary-address-view-element-${element.id}`}
        rowTestIdPrefix="contractor-primary-address-field"
        elementType="contractor_primary_address_view"
      />
    );
  },
};

/** Edytor konfiguracji bloku — checkboxy „pokaż/ukryj" per pole adresu (panel właściwości). */
function ContractorPrimaryAddressConfigEditor({
  settings,
  onChange,
}: {
  settings: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const hidden = new Set(parseHiddenAddressCoreFields(settings));

  function toggle(field: AddressFieldKey, visible: boolean) {
    const next = new Set(hidden);
    if (visible) next.delete(field);
    else next.add(field);
    onChange({ ...settings, hiddenFields: Array.from(next) });
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-text-muted">
        {i18n.t('tenant.contractors.primaryAddressBlock.configHint', {
          defaultValue: 'Odznacz pole, aby ukryć je w formularzu.',
        })}
      </p>
      {ADDRESS_CORE_FIELD_ORDER.map((field) => (
        <Checkbox
          key={field}
          label={fieldLabel(field)}
          checked={!hidden.has(field)}
          onChange={(e) => toggle(field, e.target.checked)}
        />
      ))}
    </div>
  );
}
